import { useState } from 'react';
import { X, Copy, ChevronRight } from 'lucide-react';
import { Badge } from '../ui/badge';
import { InvoiceViewer } from './InvoiceViewer';
import { fmtUKDate } from './inboxFormat';
import { fmtGBP } from './LineItemsTable';

/**
 * Side-by-side duplicate review. Left = the invoice being processed, right =
 * the matched invoice (pick which, when several). Each side shows the full
 * scrollable document plus the key fields the duplicate check keys off
 * (supplier · invoice number · value) so the user can eyeball them together.
 */
export function DuplicateCompareDialog({ api, current, duplicates = [], onClose }) {
  const [idx, setIdx] = useState(0);
  const dup = duplicates[idx] || null;

  const Field = ({ label, value, strong }) => (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-[11px] uppercase tracking-wider text-gray-500 shrink-0">{label}</span>
      <span className={`text-right ${strong ? 'font-semibold text-white' : 'text-gray-200'} text-sm truncate`}>{value || '—'}</span>
    </div>
  );

  const Side = ({ title, badge, data }) => (
    <div className="flex-1 min-w-0 flex flex-col border border-white/10 rounded-lg overflow-hidden bg-[#0F141B]">
      <div className="px-3 py-2 border-b border-white/10 bg-[#1E2530] flex items-center gap-2 shrink-0">
        <span className="text-xs font-semibold text-gray-200 truncate">{title}</span>
        {badge}
      </div>
      <div className="px-3 py-2 border-b border-white/10 bg-white/[0.02] shrink-0 space-y-0.5">
        <Field label="Supplier" value={data.supplier_name} strong />
        <Field label="Invoice no." value={data.reference} strong />
        <Field label="Value" value={typeof data.value_pence === 'number' ? fmtGBP(data.value_pence) : null} strong />
        <Field label="Invoice date" value={data.invoice_date ? fmtUKDate(data.invoice_date) : null} />
        <Field label="File" value={data.filename} />
      </div>
      <div className="flex-1 min-h-0">
        {data.id ? (
          <InvoiceViewer api={api} itemId={data.id} pageCount={data.page_count || 1} filename={data.filename || 'invoice'} />
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-gray-500">No document</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex flex-col p-3 sm:p-5" data-testid="dup-compare-dialog">
      <div className="flex items-center gap-3 mb-3 shrink-0">
        <Copy className="w-5 h-5 text-amber-400" />
        <div className="min-w-0">
          <h2 className="text-white font-semibold text-base leading-tight">Review possible duplicate</h2>
          <p className="text-xs text-gray-400 leading-tight">Same supplier, invoice number and value — compare them side by side before posting.</p>
        </div>
        {duplicates.length > 1 && (
          <div className="ml-4 flex items-center gap-1.5 overflow-x-auto" data-testid="dup-compare-selector">
            <span className="text-[11px] text-gray-500 shrink-0">Match:</span>
            {duplicates.map((d, i) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setIdx(i)}
                className={`text-xs px-2.5 h-7 rounded-md border whitespace-nowrap transition-colors ${i === idx ? 'bg-amber-500 text-[#1E2530] border-amber-500 font-semibold' : 'bg-white/5 text-gray-300 border-white/15 hover:bg-white/10'}`}
                data-testid={`dup-compare-pick-${i}`}
              >
                {d.reference || d.supplier_name || `#${i + 1}`}
              </button>
            ))}
          </div>
        )}
        <button type="button" onClick={onClose} className="ml-auto p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 shrink-0" data-testid="dup-compare-close" title="Close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-3">
        <Side
          title="This invoice"
          badge={<Badge variant="outline" className="border-sky-500/40 text-sky-300 bg-sky-500/10">Processing</Badge>}
          data={current}
        />
        <div className="hidden lg:flex items-center text-gray-600 shrink-0"><ChevronRight className="w-5 h-5" /></div>
        {dup && (
          <Side
            title="Matched invoice"
            badge={<Badge variant="outline" className={dup.posted ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-amber-500/40 text-amber-300 bg-amber-500/10'}>{dup.posted ? 'Already posted' : 'Waiting in inbox'}</Badge>}
            data={{ id: dup.id, page_count: dup.page_count, filename: dup.original_filename, supplier_name: dup.supplier_name, reference: dup.reference, invoice_date: dup.invoice_date, value_pence: dup.value_pence }}
          />
        )}
      </div>
    </div>
  );
}
